import React, { RefObject } from 'react';
import { Retry } from '@src/components/common';
import { Empty, Spin } from 'antd';
import styled from 'styled-components';
import {
  StyledContent,
  TableWrapper,
  StyledTableEmpty,
  StyledTableEmptyTitle,
  StyledTableEmptySubtext,
} from './styled';
import TableContainer from '../ContentPanel/TableContainer';

const FooterWrapper = styled.div`
  text-align: center;
  font-size: 1em;
  font-weight: 600;
  padding: 16px;
  min-height: 60px; /* Reserve space to prevent jumping */
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TableContainerWrapper = styled.div`
  background: #fff;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;

  .ant-table-pagination {
    display: none;
  }

  .ant-table-tbody > tr:last-child > td {
    border-bottom: 1px solid #f0f0f0;
  }

  /* Enable sticky headers - let parent handle scroll */
  .ant-table-thead > tr > th {
    position: sticky;
    top: 0;
    z-index: 10;
    background: #fafafa;
  }
`;

interface ContentSectionProps {
  collapsed: boolean;
  tableWrapperRef: RefObject<HTMLDivElement>;
  handleScroll: (e: React.UIEvent<HTMLDivElement>) => void;
  alerts: any[];
  isFetching: boolean;
  hasActiveFilters: boolean;
  interval: any;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  total: number;
  isError: boolean;
  refetch: () => void;
}

const EmptyComponent: React.FC<{ hasActiveFilters: boolean }> = ({ hasActiveFilters }) => (
  <StyledTableEmpty>
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={
        <div>
          <StyledTableEmptyTitle>No alerts found</StyledTableEmptyTitle>
          <StyledTableEmptySubtext>
            {hasActiveFilters
              ? 'Try adjusting your filters to see more alerts.'
              : 'There are currently no alerts to display.'}
          </StyledTableEmptySubtext>
        </div>
      }
    />
  </StyledTableEmpty>
);

const ContentSection: React.FC<ContentSectionProps> = ({
  collapsed,
  tableWrapperRef,
  handleScroll,
  alerts,
  isFetching,
  hasActiveFilters,
  interval,
  isFetchingNextPage,
  hasNextPage,
  isError,
  refetch,
}) => {
  const tableLoading = isFetching && alerts.length === 0;
  return (
    <StyledContent collapsed={collapsed}>
      <TableWrapper ref={tableWrapperRef} onScroll={handleScroll}>
        <Retry
          auto={false}
          isLoading={tableLoading}
          showError={isError && alerts.length === 0}
          fetchData={refetch}
          mountChildAlways={true}
          progress={false}
          isEmpty={false}
        >
          {/* Show custom empty state for filtered results */}
          <TableContainerWrapper>
            <TableContainer
              alerts={alerts}
              interval={interval}
              emptyPlaceholder={
                !isError && !isFetching && <EmptyComponent hasActiveFilters={hasActiveFilters} />
              }
              serverSideFilter={true}
            />

            {/* Always render footer area for infinite scroll loading */}
            <FooterWrapper>
              {isFetchingNextPage && <Spin />}
              {/* Show "No more alerts to load" when we've reached the last page */}
              {!hasNextPage && alerts.length > 0 && !isFetchingNextPage && (
                <span style={{ color: '#8c8c8c', fontSize: '12px' }}>
                  ****No more alerts to load****
                </span>
              )}
            </FooterWrapper>
          </TableContainerWrapper>
        </Retry>
      </TableWrapper>
    </StyledContent>
  );
};

export default ContentSection;
